import {
  ColumnFiltersState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { ITestConfig } from "../../../api/models/TestConfig"
import { useApp } from "../../AppProvider"
import { PAGE_SIZE } from "../../constants"
import { usePaginationState } from "../../hooks/usePaginationState"
import { useGetTestConfigs } from "../../hooks/useTestConfigs"
import { classNames } from "../../utils/classNames"
import { flattenTestConfig } from "../../utils/testConfig"
import { Pagination } from "../common"
import { DebouncedInput } from "../common/DebouncedInput"
import { ErrorMessage } from "../common/Feedback/ErrorMessage"
import { LoadingMessage } from "../common/Feedback/LoadingMessage"
import Modal from "../common/Modal"
import { TestConfigForm } from "./TestConfigForm"

// Flattened TestConfig for use in the table
export interface TestConfigTableRow {
  _id: string
  testCode: string
  testName: string
  orgId: string
  default: boolean
  isNew?: boolean
  condition: string
  message: string
  thresholds: Record<string, number>
}

const columnHelper = createColumnHelper<TestConfigTableRow>()
const columns = [
  columnHelper.accessor("testName", {
    header: "Test",
    cell: (info) => <span className="font-semibold text-gray-900">{info.getValue()}</span>,
  }),
  columnHelper.accessor("testCode", {
    header: "Code",
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  columnHelper.accessor("isNew", {
    header: "New",
    cell: (info) =>
      info.getValue() ? (
        <span className="badge badge-green">Yes</span>
      ) : (
        <span className="badge badge-gray">No</span>
      ),
  }),
  columnHelper.accessor("condition", {
    header: "Condition",
  }),
  columnHelper.accessor("thresholds", {
    header: "Thresholds",
    cell: (info) => (
      <span>
        {Object.entries(info.getValue()).map(([key, value]) => (
          <div key={key}>
            {key}: {value}
          </div>
        ))}
      </span>
    ),
  }),
  columnHelper.accessor("message", {
    header: "Message",
    cell: (info) => <span className="line-clamp-4 max-w-[75ch]">{info.getValue()}</span>,
  }),
  columnHelper.display({
    id: "actions",
    cell: (props: any) => {
      return (
        <span className="flex items-center justify-end gap-x-2">
          <button
            onClick={() => props.setFormState({ isOpen: true, config: props.row.original })}
            className="text-primary-600 hover:text-primary-900 text-sm font-medium">
            Edit
          </button>
        </span>
      )
    },
  }),
]

interface Props {
  orgId?: string
}

export const TestConfigTable = ({ orgId }: Props) => {
  // General
  const { token, user } = useApp()
  const { organizationId } = useParams()
  const isSuperAdmin = Boolean(user?.superAdmin && !organizationId)
  const navigate = useNavigate()

  const { setPage, paginationState } = usePaginationState({
    paramName: "configsPage",
    pageSize: PAGE_SIZE,
  })

  // Table Data
  const configsQuery = useGetTestConfigs(token, orgId)
  const data = useMemo(() => {
    return configsQuery?.data ? flattenTestConfig(configsQuery.data) : []
  }, [configsQuery.data])
  const [search, setSearch] = useState("")
  const [filter, setFilter] = useState("all")
  // Default filters to show only new configs for super admins
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    isSuperAdmin
      ? [
          {
            id: "isNew",
            value: true,
          },
        ]
      : [],
  )
  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter: search,
      columnVisibility: {
        isNew: isSuperAdmin, // Only show new column to super admins
      },
      columnFilters,
      pagination: paginationState,
    },
    onGlobalFilterChange: setSearch,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const newState = updater(paginationState)
        setPage(newState.pageIndex + 1)
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const handleFilterChange = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    const value = e.currentTarget.value
    const newColumnFilters = value === "all" ? [] : [{ id: "isNew", value: value === "new" }]
    setColumnFilters(newColumnFilters)
    setFilter(value)
  }

  const [formState, setFormState] = useState<{
    isOpen: boolean
    config?: ITestConfig
  }>({
    isOpen: false,
  })

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div className="text-lg font-semibold text-gray-900">Lab Values</div>
        <div className="flex items-stretch gap-x-3">
          {isSuperAdmin && (
            <div>
              <label htmlFor="newFilter" className="label">
                <span className="sr-only">Filter</span>
                <select
                  name="newFilter"
                  id="newFilter"
                  className="input pr-8"
                  value={filter}
                  onChange={handleFilterChange}>
                  <option value="all">All</option>
                  <option value="new">New</option>
                  <option value="not_new">Not New</option>
                </select>
              </label>
            </div>
          )}
          <div>
            <label htmlFor="search" className="label">
              <span className="sr-only">Search</span>
            </label>
            <DebouncedInput
              name="search"
              type="search"
              className="input"
              placeholder="Search"
              value={search}
              onChange={(value) => setSearch(value)}
            />
          </div>
        </div>
      </div>
      {configsQuery.isLoading ? (
        <LoadingMessage />
      ) : configsQuery.isError ? (
        <ErrorMessage message={configsQuery?.error?.message ?? "Unknown application error"} />
      ) : data && data.length > 0 ? (
        <>
          <div className="mt-4 flow-root overflow-hidden">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header, i) => (
                          <th
                            scope="col"
                            className={classNames(
                              "relative py-3.5 text-left text-sm font-semibold text-gray-900",
                              i === 0
                                ? "pr-3 pl-4 sm:pl-0"
                                : i === headerGroup.headers.length - 1
                                  ? "pr-4 pl-3 sm:pr-0"
                                  : "px-3",
                            )}
                            key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {table.getRowModel().rows.length > 0 ? (
                      table.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell, i) => (
                            <td
                              className={classNames(
                                "font-regular word-wrap py-4 align-top text-sm text-gray-500",
                                i === 0
                                  ? "pr-3 pl-4 sm:pl-0"
                                  : i === row.getVisibleCells().length - 1
                                    ? "pr-4 pl-3 sm:pr-0"
                                    : "px-3",
                              )}
                              key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, {
                                ...cell.getContext(),
                                navigate,
                                setFormState,
                              })}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          className="font-regular px-4 py-4 text-center text-sm whitespace-nowrap text-gray-500"
                          colSpan={table.getHeaderGroups()?.[0]?.headers?.length || 100}>
                          No results found for the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div>
            <Pagination
              page={table.getState().pagination.pageIndex + 1}
              pageCount={table.getPageCount()}
              hasPrevious={table.getCanPreviousPage()}
              onPrevious={() => table.previousPage()}
              hasNext={table.getCanNextPage()}
              onNext={() => table.nextPage()}
              setPage={(i) => table.setPageIndex(i - 1)}
            />
          </div>
        </>
      ) : (
        <div className="mt-4 text-gray-600">No results found</div>
      )}

      <Modal
        open={formState.isOpen}
        setOpen={(val) => setFormState({ ...formState, isOpen: val as boolean })}>
        {formState.config ? (
          <TestConfigForm config={formState.config} submitCb={() => setFormState({ isOpen: false })} />
        ) : undefined}
      </Modal>
    </div>
  )
}
